import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Check, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import type { LeaveRequest } from '../../services/leaveRequestService';
import { leaveDayCount, leaveHoursDisplay, typeIcons, typeStyles } from './leave-request.utils';

interface LeaveApprovalRowProps {
  request: LeaveRequest;
  /** Pending rows carry the decision buttons; history rows carry the decision taken. */
  variant?: 'pending' | 'history';
  onApprove?: (request: LeaveRequest) => void;
  onReject?: (request: LeaveRequest) => void;
  disabled?: boolean;
}

/**
 * One line of an approval list: who, what, when on the left, the decision on the right.
 * Deliberately thinner than {@link LeaveRequestCard}, which stays the self-service view.
 */
export const LeaveApprovalRow: React.FC<LeaveApprovalRowProps> = ({
  request,
  variant = 'pending',
  onApprove,
  onReject,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('it') ? it : enUS;
  const TypeIcon = typeIcons[request.type];
  const typeStyle = typeStyles[request.type];

  const days = leaveDayCount(request);
  const start = new Date(request.startDate);
  const end = new Date(request.endDate);
  const sameDay = request.startDate === request.endDate;
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  const dateRange = sameDay
    ? format(start, 'd MMM yyyy', { locale: dateLocale })
    : sameMonth
      ? `${format(start, 'd', { locale: dateLocale })} – ${format(end, 'd MMM yyyy', { locale: dateLocale })}`
      : `${format(start, 'd MMM', { locale: dateLocale })} – ${format(end, 'd MMM yyyy', { locale: dateLocale })}`;

  const durationLabel = (() => {
    if (request.durationType === 'hours') return t('leave.duration.hours', 'Hours');
    if (request.durationType === 'half_day_morning') return t('leave.duration.halfMorning', 'Half day (AM)');
    if (request.durationType === 'half_day_afternoon') return t('leave.duration.halfAfternoon', 'Half day (PM)');
    return days === 1
      ? t('leave.duration.oneDay', '1 day')
      : t('leave.duration.days', '{{count}} days', { count: days });
  })();

  const category = request.category ?? request.changeType;
  const categoryLabel =
    category === 'amendment'
      ? t('leave.myRequests.categories.amendment', 'Amendment')
      : category === 'cancellation'
        ? t('leave.myRequests.categories.cancellation', 'Cancellation')
        : null;

  const decidedAt = request.reviewedAt ?? request.updatedAt;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 px-3 py-2.5 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:gap-4',
        disabled && 'opacity-60',
      )}
    >
      {/* Who / what / when */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            typeStyle.container,
          )}
        >
          <TypeIcon className={cn('h-4 w-4', typeStyle.icon)} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-medium">
              {request.user?.name ?? t('common.unknown', 'Unknown')}
            </span>
            {categoryLabel && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium uppercase tracking-wide">
                {categoryLabel}
              </Badge>
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span>{t(typeStyle.labelKey, typeStyle.defaultLabel)}</span>
            <span aria-hidden className="text-muted-foreground/40">·</span>
            <span className="tabular-nums">{dateRange}</span>
            <span aria-hidden className="text-muted-foreground/40">·</span>
            <span className="tabular-nums">
              {durationLabel} ({leaveHoursDisplay(request)})
            </span>
          </div>

          {request.reason && (
            <p className="mt-0.5 truncate text-xs italic text-muted-foreground" title={request.reason}>
              “{request.reason}”
            </p>
          )}

          {request.state === 'rejected' && request.rejectionReason && (
            <p
              className="mt-0.5 truncate text-xs text-red-600 dark:text-red-400"
              title={request.rejectionReason}
            >
              {t('leave.rejectedReason', 'Rejected')}: {request.rejectionReason}
            </p>
          )}
        </div>
      </div>

      {/* Decision */}
      {variant === 'pending' ? (
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          {onApprove && (
            <Button
              size="sm"
              className="h-8 bg-green-600 px-3 hover:bg-green-700"
              disabled={disabled}
              onClick={() => onApprove(request)}
            >
              <Check className="mr-1 h-4 w-4" />
              {t('leave.approve', 'Approve')}
            </Button>
          )}
          {onReject && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-red-200 px-3 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-900 dark:hover:bg-red-950"
              disabled={disabled}
              onClick={() => onReject(request)}
            >
              <X className="mr-1 h-4 w-4" />
              {t('leave.reject', 'Reject')}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex shrink-0 items-center gap-3 self-end sm:self-auto">
          <div className="text-right text-xs text-muted-foreground">
            <div className="tabular-nums">{format(new Date(decidedAt), 'd MMM yyyy', { locale: dateLocale })}</div>
            {request.reviewer?.name && (
              <div className="truncate" title={request.reviewer.name}>
                {request.reviewer.name}
              </div>
            )}
          </div>
          <Badge variant={request.state} className="h-5 shrink-0 px-2 text-[11px]">
            {t(`leave.state.${request.state}`, request.state)}
          </Badge>
        </div>
      )}
    </div>
  );
};
